import { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
    Users, FolderKanban, Coins, Activity, Shield, ShieldOff, ArrowUpRight,
    UserCheck, X, Trash2, Settings as SettingsIcon, RefreshCw, Eye,
    ChevronRight, Mail, BarChart3, AlertCircle, KeyRound, Users2, Zap, Gauge,
    Hourglass, Wallet,
} from "lucide-react";
import InfluencerLibraryPanel from "@/components/admin/InfluencerLibraryPanel";
import LeaseAdminTab from "@/components/admin/LeaseAdminTab";
import ProdResetTab from "@/components/admin/ProdResetTab";
import LlmCostPanel from "@/components/admin/LlmCostPanel";
import { useAuth } from "@/context/AuthContext";

/** ───────── Phase C.3 — Admin Control Panel ─────────
 *  Completely separate from the user dashboard. Five sections in a
 *  left-rail "OS control panel" layout:
 *    1. Overview (analytics)
 *    2. Approvals (pending users)
 *    3. Users (full CRUD + plans + quotas + delete)
 *    4. Projects (cross-owner view)
 *    5. Activity (raw agent run telemetry)
 *
 *  Pure governance UI — no pipeline / agent / orchestrator changes.
 *  Anti-leakage: tokens, model names, and API spend are explicitly admin
 *  surface only and never bubble to the user dashboard. */
const SECTIONS = [
    { key: "overview",  label: "Overview",  icon: BarChart3 },
    { key: "approvals", label: "Approvals", icon: UserCheck },
    { key: "users",     label: "Users",     icon: Users },
    { key: "projects",  label: "Projects",  icon: FolderKanban },
    { key: "influencers", label: "Influencers", icon: Users2 },
    { key: "llm",       label: "LLM Health", icon: Zap },
    { key: "llmcost",   label: "LLM Cost",  icon: Wallet },
    { key: "telemetry", label: "Telemetry", icon: Gauge },
    { key: "lease",     label: "Lease",     icon: Hourglass },
    { key: "prodreset", label: "Prod Reset", icon: Trash2 },
    { key: "activity",  label: "Activity",  icon: Activity },
];

export default function Admin() {
    const { user } = useAuth();
    const [section, setSection] = useState("overview");
    const [stats, setStats] = useState(null);
    const [users, setUsers] = useState(null);
    const [detailUserId, setDetailUserId] = useState(null);

    const reloadStats = () =>
        api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
    const reloadUsers = () =>
        api.get("/admin/users").then(({ data }) => setUsers(data)).catch(() => setUsers([]));

    useEffect(() => { reloadStats(); reloadUsers(); }, []);

    if (!user) return <Navigate to="/login" replace />;
    if (!user.is_admin) return <Navigate to="/dashboard" replace />;

    const reloadAll = async () => { await Promise.all([reloadStats(), reloadUsers()]); };

    return (
        <div className="min-h-screen bg-[#050505] text-white" data-testid="admin-page">
            <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] min-h-screen">
                {/* Left rail */}
                <aside className="border-r border-zinc-900 bg-[#070707] flex flex-col">
                    <div className="p-5 border-b border-zinc-900">
                        <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-sm bg-[#FF4400]/15 border border-[#FF4400]/40 flex items-center justify-center">
                                <Shield className="w-4 h-4 text-[#FF4400]" />
                            </div>
                            <div>
                                <div className="font-display text-base leading-none">Control</div>
                                <div className="label-micro mt-1 text-[#FF4400]">System Admin</div>
                            </div>
                        </div>
                    </div>
                    <nav className="flex-1 p-3 space-y-1">
                        {SECTIONS.map(s => {
                            const isActive = section === s.key;
                            const badge =
                                s.key === "approvals" && stats?.pending_users > 0
                                    ? stats.pending_users
                                    : null;
                            return (
                                <button
                                    key={s.key}
                                    type="button"
                                    data-testid={`admin-nav-${s.key}`}
                                    onClick={() => setSection(s.key)}
                                    className={
                                        "w-full text-left flex items-center gap-3 px-3 py-2.5 text-sm rounded-sm border transition-colors " +
                                        (isActive
                                            ? "border-[#FF4400]/50 bg-[#FF4400]/8 text-white"
                                            : "border-transparent text-zinc-400 hover:text-white hover:bg-zinc-900/60")
                                    }
                                >
                                    <s.icon className="w-4 h-4" />
                                    <span className="flex-1">{s.label}</span>
                                    {badge != null && (
                                        <span className="px-1.5 py-0.5 text-[10px] font-mono rounded-sm bg-amber-500/20 border border-amber-500/40 text-amber-300">
                                            {badge}
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </nav>
                    <div className="p-3 border-t border-zinc-900 space-y-2">
                        <Link
                            to="/dashboard"
                            data-testid="admin-exit"
                            className="flex items-center gap-2 px-3 py-2 text-xs font-mono uppercase tracking-wider text-zinc-500 hover:text-white border border-zinc-900 hover:border-zinc-700 rounded-sm transition-colors"
                        >
                            <ChevronRight className="w-3 h-3 rotate-180" /> Exit to user dashboard
                        </Link>
                        <button
                            type="button"
                            onClick={reloadAll}
                            data-testid="admin-refresh"
                            className="w-full flex items-center gap-2 px-3 py-2 text-xs font-mono uppercase tracking-wider text-zinc-500 hover:text-white"
                        >
                            <RefreshCw className="w-3 h-3" /> Refresh
                        </button>
                    </div>
                </aside>

                {/* Main */}
                <main className="p-6 sm:p-8 overflow-x-hidden">
                    <header className="flex items-end justify-between gap-6 mb-8 flex-wrap">
                        <div>
                            <div className="label-micro mb-2">System Governance</div>
                            <h1 className="font-display text-3xl sm:text-4xl font-medium tracking-tight">
                                {SECTIONS.find(s => s.key === section)?.label}
                            </h1>
                        </div>
                        {stats && (
                            <div className="flex items-center gap-3 text-xs font-mono text-zinc-500">
                                <span>{stats.total_users} users</span>
                                <span className="text-zinc-700">·</span>
                                <span className="text-amber-400">{stats.pending_users || 0} pending</span>
                                <span className="text-zinc-700">·</span>
                                <span>{stats.total_projects} projects</span>
                                <span className="text-zinc-700">·</span>
                                <span>{stats.total_runs} runs</span>
                            </div>
                        )}
                    </header>

                    {section === "overview"  && <OverviewSection stats={stats} users={users} onJump={setSection} />}
                    {section === "approvals" && <ApprovalsSection users={users} onChange={reloadAll} onOpenDetail={setDetailUserId} />}
                    {section === "users"     && <UsersSection users={users} onChange={reloadAll} onOpenDetail={setDetailUserId} />}
                    {section === "projects"  && <ProjectsSection />}
                    {section === "influencers" && <InfluencerLibraryPanel />}
                    {section === "llm"       && <LLMHealthSection />}
                    {section === "llmcost"   && <LlmCostPanel />}
                    {section === "telemetry" && <TelemetrySection />}
                    {section === "lease"     && <LeaseAdminTab />}
                    {section === "prodreset" && <ProdResetTab />}
                    {section === "activity"  && <ActivitySection />}
                </main>
            </div>

            {detailUserId && (
                <UserDetailDrawer
                    userId={detailUserId}
                    onClose={() => setDetailUserId(null)}
                    onChange={reloadAll}
                />
            )}
        </div>
    );
}

/* ─────────────────────────── Overview ─────────────────────────── */

function OverviewSection({ stats, users, onJump }) {
    if (!stats || !users) return <Loading />;
    const activeUsers = users.filter(u => u.status === "active").length;
    const pendingUsers = users.filter(u => u.status === "pending").length;
    return (
        <div className="space-y-6" data-testid="admin-overview">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                <Stat label="Total Users"    value={stats.total_users} />
                <Stat label="Active"         value={activeUsers}        accent="emerald" onClick={() => onJump("users")} />
                <Stat label="Pending"        value={pendingUsers}       accent="amber"   onClick={() => onJump("approvals")} />
                <Stat label="Total Projects" value={stats.total_projects} onClick={() => onJump("projects")} />
                <Stat label="Total AI Runs"  value={stats.total_runs}    onClick={() => onJump("activity")} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="card-base p-5 lg:col-span-2">
                    <div className="label-micro mb-3">Plan Breakdown</div>
                    <PlanBars
                        trial={stats.plan_breakdown.trial}
                        enterprise={stats.plan_breakdown.enterprise}
                        none={stats.plan_breakdown.none}
                    />
                </div>
                <div className="card-base p-5">
                    <div className="label-micro mb-2">Pending Queue</div>
                    {pendingUsers === 0 ? (
                        <div className="text-sm text-zinc-500 mt-2">All users approved.</div>
                    ) : (
                        <button
                            onClick={() => onJump("approvals")}
                            data-testid="admin-overview-jump-approvals"
                            className="mt-2 w-full text-left p-3 border border-amber-500/30 bg-amber-500/5 rounded-sm hover:border-amber-500/60 transition-colors"
                        >
                            <div className="font-display text-3xl text-amber-300">{pendingUsers}</div>
                            <div className="text-xs text-amber-400 mt-1">awaiting your approval</div>
                            <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mt-3 flex items-center gap-1">
                                Review now <ArrowUpRight className="w-2.5 h-2.5" />
                            </div>
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

function Stat({ label, value, accent = "default", onClick }) {
    const accentClass =
        accent === "emerald" ? "border-emerald-500/30 hover:border-emerald-500/60" :
        accent === "amber"   ? "border-amber-500/30 hover:border-amber-500/60" :
                               "border-zinc-900 hover:border-zinc-700";
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={!onClick}
            className={`card-base p-4 text-left ${accentClass} ${onClick ? "cursor-pointer" : "cursor-default"} card-hover`}
        >
            <div className="label-micro mb-2">{label}</div>
            <div className="font-display text-3xl font-medium">{value ?? "—"}</div>
        </button>
    );
}

function PlanBars({ trial, enterprise, none }) {
    const total = Math.max(1, (trial || 0) + (enterprise || 0) + (none || 0));
    const Row = ({ label, value, color }) => (
        <div>
            <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-zinc-300">{label}</span>
                <span className="font-mono text-zinc-200">{value || 0}</span>
            </div>
            <div className="h-1.5 bg-zinc-900 rounded-sm overflow-hidden">
                <div className="h-full transition-all" style={{ width: `${((value || 0) / total) * 100}%`, background: color }} />
            </div>
        </div>
    );
    return (
        <div className="space-y-3 mt-2">
            <Row label="Enterprise"  value={enterprise} color="#FF4400" />
            <Row label="Trial"       value={trial}      color="#F59E0B" />
            <Row label="No plan"     value={none}       color="#52525b" />
        </div>
    );
}

/* ─────────────────────────── Approvals ─────────────────────────── */

function ApprovalsSection({ users, onChange, onOpenDetail }) {
    const [busy, setBusy] = useState(null);
    const pending = useMemo(() => (users || []).filter(u => u.status === "pending"), [users]);

    const approve = async (id) => {
        setBusy(id);
        try {
            await api.post(`/admin/users/${id}/status`, { status: "active" });
            toast.success("User approved.");
            await onChange();
        } catch (e) { toast.error(e?.userMessage || "Failed"); }
        finally { setBusy(null); }
    };
    const reject = async (id, email) => {
        if (!window.confirm(`Reject ${email}? This deletes their account permanently.`)) return;
        setBusy(id);
        try {
            await api.delete(`/admin/users/${id}`);
            toast.success("User rejected and deleted.");
            await onChange();
        } catch (e) { toast.error(e?.userMessage || "Failed"); }
        finally { setBusy(null); }
    };

    if (!users) return <Loading />;
    if (pending.length === 0) {
        return (
            <div className="card-base p-10 text-center" data-testid="admin-approvals-empty">
                <UserCheck className="w-10 h-10 mx-auto text-emerald-400 mb-3" />
                <div className="font-display text-xl">All clear</div>
                <div className="text-sm text-zinc-500 mt-2">No users awaiting approval.</div>
            </div>
        );
    }
    return (
        <div className="space-y-3" data-testid="admin-approvals">
            <div className="text-xs font-mono uppercase tracking-wider text-amber-400 mb-2">
                {pending.length} user{pending.length === 1 ? "" : "s"} awaiting approval
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {pending.map(u => (
                    <div
                        key={u.id}
                        data-testid={`admin-approval-${u.id}`}
                        className="card-base p-5 border-amber-500/20 hover:border-amber-500/40 transition-colors"
                    >
                        <div className="flex items-start justify-between gap-3 mb-4">
                            <div className="min-w-0">
                                <div className="font-display text-lg truncate">{u.name || u.email.split("@")[0]}</div>
                                <div className="text-xs text-zinc-500 truncate flex items-center gap-1.5 mt-0.5">
                                    <Mail className="w-3 h-3" /> {u.email}
                                </div>
                                {u.signup?.company_name && (
                                    <div className="text-xs text-zinc-400 mt-2">
                                        {u.signup.company_name}
                                        {u.signup.usage_type ? ` · ${u.signup.usage_type}` : ""}
                                        {u.signup.scale ? ` · ${u.signup.scale}` : ""}
                                    </div>
                                )}
                            </div>
                            <span className="px-2 py-0.5 rounded-sm border border-amber-500/40 text-amber-400 text-[10px] font-mono uppercase tracking-wider shrink-0">
                                Pending
                            </span>
                        </div>
                        <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-600 mb-4">
                            Signed up {timeAgo(u.created_at)}
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                            <button
                                type="button"
                                data-testid={`admin-approve-${u.id}`}
                                disabled={busy === u.id}
                                onClick={() => approve(u.id)}
                                className="btn-accent text-xs"
                            >
                                <UserCheck className="w-3.5 h-3.5" /> Approve
                            </button>
                            <button
                                type="button"
                                data-testid={`admin-reject-${u.id}`}
                                disabled={busy === u.id}
                                onClick={() => reject(u.id, u.email)}
                                className="px-3 py-2 text-xs border border-red-500/40 hover:bg-red-500/10 rounded-sm text-red-400 disabled:opacity-30 flex items-center gap-1.5"
                            >
                                <Trash2 className="w-3.5 h-3.5" /> Reject
                            </button>
                            <button
                                type="button"
                                data-testid={`admin-detail-${u.id}`}
                                onClick={() => onOpenDetail(u.id)}
                                className="ml-auto text-xs font-mono text-zinc-400 hover:text-white flex items-center gap-1"
                            >
                                <Eye className="w-3 h-3" /> Details
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

/* ─────────────────────────── Users ─────────────────────────── */

function UsersSection({ users, onChange, onOpenDetail }) {
    const [busy, setBusy] = useState(null);
    const [filter, setFilter] = useState("all"); // all | active | pending
    const [planUser, setPlanUser] = useState(null); // user obj for custom-plan dialog
    const [pwUser, setPwUser] = useState(null); // user obj for password-reset dialog

    if (!users) return <Loading />;

    const visible = users.filter(u => filter === "all" || u.status === filter);

    const setPlan = async (id, plan) => {
        setBusy(id);
        try {
            await api.post(`/admin/users/${id}/plan`, { plan });
            toast.success(plan === "none" ? "Plan removed." : `Assigned ${plan} (auto-approved).`);
            await onChange();
        } catch (e) { toast.error(e?.userMessage || "Failed"); }
        finally { setBusy(null); }
    };

    const setStatus = async (id, status) => {
        setBusy(id);
        try {
            await api.post(`/admin/users/${id}/status`, { status });
            toast.success(status === "active" ? "User approved." : "User suspended.");
            await onChange();
        } catch (e) { toast.error(e?.userMessage || "Failed"); }
        finally { setBusy(null); }
    };

    const resetQuota = async (id) => {
        setBusy(id);
        try {
            await api.post(`/admin/users/${id}/quota`, { client_quota: 0, campaign_quota: 0 });
            toast.success("Quota reset to zero.");
            await onChange();
        } catch (e) { toast.error(e?.userMessage || "Failed"); }
        finally { setBusy(null); }
    };

    const toggleAdmin = async (u) => {
        if (!window.confirm(u.is_admin ? `Demote ${u.email} from admin?` : `Promote ${u.email} to admin?`)) return;
        setBusy(u.id);
        try {
            await api.post(`/admin/users/${u.id}/admin`, { is_admin: !u.is_admin });
            toast.success("Updated.");
            await onChange();
        } catch (e) { toast.error(e?.userMessage || "Failed"); }
        finally { setBusy(null); }
    };

    const removeUser = async (u) => {
        if (!window.confirm(`Delete ${u.email}? Cascades all their clients, projects, and history. Cannot undo.`)) return;
        setBusy(u.id);
        try {
            await api.delete(`/admin/users/${u.id}`);
            toast.success("User deleted.");
            await onChange();
        } catch (e) { toast.error(e?.userMessage || "Failed"); }
        finally { setBusy(null); }
    };

    return (
        <div data-testid="admin-users">
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-1">
                    {["all", "active", "pending"].map(k => (
                        <button
                            key={k}
                            type="button"
                            data-testid={`admin-users-filter-${k}`}
                            onClick={() => setFilter(k)}
                            className={
                                "text-[10px] font-mono uppercase tracking-wider px-2 py-1 border rounded-sm transition-colors " +
                                (filter === k
                                    ? "border-[#FF4400]/60 text-[#FF4400] bg-[#FF4400]/8"
                                    : "border-zinc-800 text-zinc-400 hover:text-white")
                            }
                        >
                            {k}
                        </button>
                    ))}
                </div>
                <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-600">{visible.length} shown</span>
            </div>
            <div className="card-base overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-zinc-950 text-[10px] font-mono uppercase tracking-wider text-zinc-500">
                        <tr>
                            <th className="text-left px-4 py-2">User</th>
                            <th className="text-left px-4 py-2">Company</th>
                            <th className="text-left px-4 py-2">Status</th>
                            <th className="text-left px-4 py-2">Plan</th>
                            <th className="text-left px-4 py-2">Clients</th>
                            <th className="text-left px-4 py-2">Campaigns</th>
                            <th className="text-left px-4 py-2">Created</th>
                            <th className="text-right px-4 py-2">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {visible.map(u => (
                            <tr key={u.id} data-testid={`admin-user-${u.id}`} className="border-t border-zinc-900 hover:bg-zinc-950/50">
                                <td className="px-4 py-3">
                                    <button
                                        type="button"
                                        onClick={() => onOpenDetail(u.id)}
                                        data-testid={`admin-user-name-${u.id}`}
                                        className="text-left hover:text-white"
                                    >
                                        <div className="font-medium">{u.name || u.email.split("@")[0]}</div>
                                        <div className="text-xs text-zinc-500">{u.email}</div>
                                    </button>
                                </td>
                                <td className="px-4 py-3 text-xs text-zinc-400 truncate max-w-[180px]">
                                    {u.signup?.company_name || "—"}
                                </td>
                                <td className="px-4 py-3"><StatusBadge status={u.status} /></td>
                                <td className="px-4 py-3"><PlanBadge plan={u.plan} label={u.plan_label} /></td>
                                <td className="px-4 py-3 font-mono text-xs">{u.clients_used}/{u.client_quota}</td>
                                <td className="px-4 py-3 font-mono text-xs">{u.campaigns_used}/{u.campaign_quota}</td>
                                <td className="px-4 py-3 text-[11px] font-mono text-zinc-500">{timeAgo(u.created_at)}</td>
                                <td className="px-4 py-3 text-right">
                                    <div className="inline-flex items-center gap-1 flex-wrap justify-end">
                                        {u.status === "pending" && (
                                            <button
                                                data-testid={`admin-quick-approve-${u.id}`}
                                                disabled={busy === u.id}
                                                onClick={() => setStatus(u.id, "active")}
                                                className="px-2 py-1 text-[10px] font-mono uppercase tracking-wider border border-emerald-500/60 hover:bg-emerald-500/10 rounded-sm text-emerald-400 disabled:opacity-30"
                                            >
                                                Approve
                                            </button>
                                        )}
                                        {u.status === "active" && !u.is_admin && (
                                            <button
                                                data-testid={`admin-suspend-${u.id}`}
                                                disabled={busy === u.id}
                                                onClick={() => setStatus(u.id, "pending")}
                                                className="px-2 py-1 text-[10px] font-mono uppercase tracking-wider border border-zinc-800 hover:bg-zinc-900 rounded-sm text-zinc-400 disabled:opacity-30"
                                            >
                                                Suspend
                                            </button>
                                        )}
                                        <button
                                            data-testid={`admin-plan-${u.id}-trial`}
                                            disabled={busy === u.id}
                                            onClick={() => setPlan(u.id, "trial")}
                                            className="px-2 py-1 text-[10px] font-mono uppercase tracking-wider border border-amber-500/40 hover:bg-amber-500/10 rounded-sm text-amber-400 disabled:opacity-30"
                                            title="Trial · 1 client / 3 campaigns"
                                        >
                                            Trial
                                        </button>
                                        <button
                                            data-testid={`admin-plan-${u.id}-enterprise`}
                                            disabled={busy === u.id}
                                            onClick={() => setPlan(u.id, "enterprise")}
                                            className="px-2 py-1 text-[10px] font-mono uppercase tracking-wider border border-[#FF4400]/40 hover:bg-[#FF4400]/10 rounded-sm text-[#FF4400] disabled:opacity-30"
                                            title="Enterprise · 5 clients / 20 campaigns"
                                        >
                                            Ent.
                                        </button>
                                        <button
                                            data-testid={`admin-custom-${u.id}`}
                                            disabled={busy === u.id}
                                            onClick={() => setPlanUser(u)}
                                            className="px-2 py-1 text-[10px] font-mono uppercase tracking-wider border border-zinc-800 hover:bg-zinc-900 rounded-sm text-zinc-400 disabled:opacity-30"
                                            title="Custom quotas"
                                        >
                                            <SettingsIcon className="w-3 h-3" />
                                        </button>
                                        <button
                                            data-testid={`admin-reset-${u.id}`}
                                            disabled={busy === u.id}
                                            onClick={() => resetQuota(u.id)}
                                            className="px-2 py-1 text-[10px] font-mono uppercase tracking-wider border border-zinc-800 hover:bg-zinc-900 rounded-sm text-zinc-400 disabled:opacity-30"
                                            title="Reset quota to 0"
                                        >
                                            <RefreshCw className="w-3 h-3" />
                                        </button>
                                        <button
                                            data-testid={`admin-reset-pw-${u.id}`}
                                            disabled={busy === u.id}
                                            onClick={() => setPwUser(u)}
                                            className="px-2 py-1 text-[10px] font-mono uppercase tracking-wider border border-sky-500/40 hover:bg-sky-500/10 rounded-sm text-sky-300 disabled:opacity-30"
                                            title="Reset password"
                                        >
                                            <KeyRound className="w-3 h-3" />
                                        </button>
                                        <button
                                            data-testid={`admin-toggle-${u.id}`}
                                            disabled={busy === u.id}
                                            onClick={() => toggleAdmin(u)}
                                            className="px-2 py-1 text-[10px] font-mono uppercase tracking-wider border border-zinc-800 hover:bg-zinc-900 rounded-sm text-zinc-400 disabled:opacity-30"
                                            title={u.is_admin ? "Demote" : "Promote to admin"}
                                        >
                                            {u.is_admin ? <ShieldOff className="w-3 h-3" /> : <Shield className="w-3 h-3" />}
                                        </button>
                                        <button
                                            data-testid={`admin-delete-${u.id}`}
                                            disabled={busy === u.id}
                                            onClick={() => removeUser(u)}
                                            className="px-2 py-1 text-[10px] font-mono uppercase tracking-wider border border-red-500/40 hover:bg-red-500/10 rounded-sm text-red-400 disabled:opacity-30"
                                            title="Delete user (cascades data)"
                                        >
                                            <Trash2 className="w-3 h-3" />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                        {visible.length === 0 && (
                            <tr><td colSpan={8} className="text-center text-zinc-500 py-10 text-sm">No matching users.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {planUser && (
                <CustomPlanDialog
                    user={planUser}
                    onClose={() => setPlanUser(null)}
                    onSaved={async () => { setPlanUser(null); await onChange(); }}
                />
            )}
            {pwUser && (
                <ResetPasswordDialog
                    user={pwUser}
                    onClose={() => setPwUser(null)}
                    onSaved={async () => { setPwUser(null); await onChange(); }}
                />
            )}
        </div>
    );
}

function CustomPlanDialog({ user, onClose, onSaved }) {
    const [plan, setPlan] = useState(user.plan || "trial");
    const [client_quota, setClientQuota] = useState(user.client_quota || 0);
    const [campaign_quota, setCampaignQuota] = useState(user.campaign_quota || 0);
    const [busy, setBusy] = useState(false);

    const save = async () => {
        setBusy(true);
        try {
            // Plan endpoint accepts overrides; auto-flips status to active.
            await api.post(`/admin/users/${user.id}/plan`, {
                plan,
                client_quota_override: Number(client_quota) || 0,
                campaign_quota_override: Number(campaign_quota) || 0,
            });
            toast.success(`Plan saved (${plan}, ${client_quota}/${campaign_quota}).`);
            await onSaved();
        } catch (e) { toast.error(e?.userMessage || "Failed"); }
        finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="admin-custom-plan-modal">
            <div className="card-base p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
                <div className="flex items-start justify-between mb-4">
                    <div>
                        <div className="label-micro mb-1">Custom Plan</div>
                        <h3 className="font-display text-xl">{user.name || user.email}</h3>
                        <div className="text-xs text-zinc-500 mt-1">{user.email}</div>
                    </div>
                    <button onClick={onClose} className="text-zinc-500 hover:text-white"><X className="w-5 h-5" /></button>
                </div>
                <div className="space-y-4">
                    <div>
                        <label className="label-micro block mb-2">Plan tier</label>
                        <div className="grid grid-cols-2 gap-2">
                            {[{k:"trial", l:"Trial"}, {k:"enterprise", l:"Enterprise"}].map(o => (
                                <button
                                    key={o.k}
                                    type="button"
                                    onClick={() => setPlan(o.k)}
                                    data-testid={`admin-custom-plan-${o.k}`}
                                    className={
                                        "p-3 border rounded-sm text-sm transition-colors " +
                                        (plan === o.k
                                            ? "border-[#FF4400] bg-[#FF4400]/5 text-white"
                                            : "border-zinc-900 hover:border-zinc-700 text-zinc-300")
                                    }
                                >{o.l}</button>
                            ))}
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="label-micro block mb-2">Client quota</label>
                            <input
                                type="number" min="0"
                                data-testid="admin-custom-clients"
                                value={client_quota}
                                onChange={e => setClientQuota(e.target.value)}
                                className="form-input font-mono"
                            />
                        </div>
                        <div>
                            <label className="label-micro block mb-2">Campaign quota</label>
                            <input
                                type="number" min="0"
                                data-testid="admin-custom-campaigns"
                                value={campaign_quota}
                                onChange={e => setCampaignQuota(e.target.value)}
                                className="form-input font-mono"
                            />
                        </div>
                    </div>
                    <p className="text-xs text-zinc-500 flex items-start gap-2">
                        <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-amber-400" />
                        Saving auto-approves the user (status → active).
                    </p>
                </div>
                <div className="flex justify-end gap-2 mt-6">
                    <button onClick={onClose} className="btn-secondary text-sm">Cancel</button>
                    <button onClick={save} disabled={busy} data-testid="admin-custom-save" className="btn-accent text-sm">
                        {busy ? "..." : "Save plan"}
                    </button>
                </div>
            </div>
        </div>
    );
}

/* ─────────────────────────── User Detail Drawer ─────────────────────────── */

function ResetPasswordDialog({ user, onClose, onSaved }) {
    const [pw, setPw] = useState("");
    const [confirm, setConfirm] = useState("");
    const [forceChange, setForceChange] = useState(true);
    const [busy, setBusy] = useState(false);
    const [adminConfirm, setAdminConfirm] = useState(false);
    const targetIsAdmin = !!user.is_admin;

    const valid = pw.length >= 8 && pw === confirm && (!targetIsAdmin || adminConfirm);

    const save = async () => {
        if (!valid) return;
        setBusy(true);
        try {
            await api.post(`/admin/users/${user.id}/reset-password`, {
                new_password: pw,
                confirm_password: confirm,
                force_change: forceChange,
                confirm_admin_reset: adminConfirm,
            });
            toast.success(
                forceChange
                    ? "Password reset — user must change it on next login."
                    : "Password reset."
            );
            await onSaved();
        } catch (e) {
            const d = e?.response?.data?.detail;
            toast.error((typeof d === "string" ? d : d?.message) || e?.userMessage || "Reset failed");
        } finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={onClose} data-testid="admin-reset-pw-modal">
            <div className="card-base p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
                <div className="flex items-start justify-between mb-4">
                    <div>
                        <div className="label-micro mb-1 flex items-center gap-2">
                            <KeyRound className="w-3.5 h-3.5 text-sky-400" />
                            <span>Reset Password</span>
                        </div>
                        <h3 className="font-display text-xl">{user.name || user.email}</h3>
                        <div className="text-xs text-zinc-500 mt-1">{user.email}</div>
                    </div>
                    <button onClick={onClose} className="text-zinc-500 hover:text-white"><X className="w-5 h-5" /></button>
                </div>

                {targetIsAdmin && (
                    <div className="mb-4 border border-amber-500/40 bg-amber-500/5 rounded-sm px-3 py-2 text-xs text-amber-200 flex items-start gap-2"
                        data-testid="admin-reset-pw-warning">
                        <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-amber-400" />
                        <span>
                            This user is an <strong>admin</strong>. Resetting an admin password will immediately
                            change their access. Confirm below to proceed.
                        </span>
                    </div>
                )}

                <div className="space-y-4">
                    <div>
                        <label className="label-micro block mb-2">New temporary password *</label>
                        <input
                            data-testid="admin-reset-pw-new"
                            type="text"
                            value={pw}
                            onChange={e => setPw(e.target.value)}
                            placeholder="Min 8 characters"
                            autoComplete="new-password"
                            className="form-input font-mono"
                        />
                        <div className="text-[10px] text-zinc-600 mt-1">
                            Shown once. Share it with the user through a secure channel.
                        </div>
                    </div>
                    <div>
                        <label className="label-micro block mb-2">Confirm password *</label>
                        <input
                            data-testid="admin-reset-pw-confirm"
                            type="text"
                            value={confirm}
                            onChange={e => setConfirm(e.target.value)}
                            autoComplete="new-password"
                            className={`form-input font-mono ${
                                confirm && pw !== confirm ? "border-red-500/50" : ""
                            }`}
                        />
                        {confirm && pw !== confirm && (
                            <div className="text-[10px] text-red-400 mt-1">Passwords do not match.</div>
                        )}
                    </div>

                    <label className="flex items-center gap-2 cursor-pointer text-sm">
                        <input
                            type="checkbox"
                            data-testid="admin-reset-pw-force"
                            checked={forceChange}
                            onChange={e => setForceChange(e.target.checked)}
                            className="w-4 h-4 accent-[#FF4400]"
                        />
                        <span>Force user to change password on next login</span>
                    </label>

                    {targetIsAdmin && (
                        <label className="flex items-center gap-2 cursor-pointer text-sm">
                            <input
                                type="checkbox"
                                data-testid="admin-reset-pw-admin-confirm"
                                checked={adminConfirm}
                                onChange={e => setAdminConfirm(e.target.checked)}
                                className="w-4 h-4 accent-[#FF4400]"
                            />
                            <span className="text-amber-200">I understand I'm resetting an admin account's password.</span>
                        </label>
                    )}
                </div>

                <div className="flex justify-end gap-2 mt-6">
                    <button onClick={onClose} className="btn-secondary text-sm">Cancel</button>
                    <button
                        onClick={save}
                        disabled={!valid || busy}
                        data-testid="admin-reset-pw-save"
                        className="btn-accent text-sm disabled:opacity-40 disabled:cursor-not-allowed">
                        {busy ? "Resetting…" : "Reset password"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function UserDetailDrawer({ userId, onClose, onChange }) {
    const [data, setData] = useState(null);
    const [pwOpen, setPwOpen] = useState(false);
    useEffect(() => {
        let mounted = true;
        api.get(`/admin/users/${userId}`).then(({ data }) => { if (mounted) setData(data); }).catch(() => mounted && setData(false));
        return () => { mounted = false; };
    }, [userId]);

    return (
        <div
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex justify-end"
            onClick={onClose}
            data-testid="admin-user-detail"
        >
            <aside
                onClick={e => e.stopPropagation()}
                className="w-full max-w-2xl h-full bg-[#0a0a0a] border-l border-zinc-900 overflow-y-auto"
            >
                {data === null && <div className="p-10 text-center text-zinc-500">Loading user…</div>}
                {data === false && (
                    <div className="p-10 text-center">
                        <div className="text-red-400 mb-2">Failed to load user.</div>
                        <button onClick={onClose} className="btn-secondary text-sm">Close</button>
                    </div>
                )}
                {data && data.user && (
                    <>
                        <div className="p-6 border-b border-zinc-900 flex items-start justify-between gap-4">
                            <div className="min-w-0">
                                <div className="label-micro mb-1">User Detail</div>
                                <h2 className="font-display text-2xl truncate">{data.user.name || data.user.email.split("@")[0]}</h2>
                                <div className="text-xs text-zinc-500 mt-1">{data.user.email}</div>
                                <div className="flex items-center gap-2 mt-3">
                                    <StatusBadge status={data.user.status} />
                                    <PlanBadge plan={data.user.plan} label={data.user.plan_label} />
                                    {data.user.is_admin && (
                                        <span className="px-2 py-0.5 rounded-sm border border-[#FF4400] text-[#FF4400] text-[10px] font-mono uppercase tracking-wider">Admin</span>
                                    )}
                                </div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                                <button
                                    onClick={() => setPwOpen(true)}
                                    data-testid="admin-drawer-reset-pw"
                                    className="px-2.5 py-1 text-[10px] font-mono uppercase tracking-wider border border-sky-500/40 hover:bg-sky-500/10 rounded-sm text-sky-300 flex items-center gap-1.5"
                                    title="Reset user's password"
                                >
                                    <KeyRound className="w-3 h-3" /> Reset password
                                </button>
                                <button onClick={onClose} className="text-zinc-500 hover:text-white"><X className="w-5 h-5" /></button>
                            </div>
                        </div>

                        <div className="p-6 grid grid-cols-3 gap-3">
                            <Stat label="Clients" value={`${data.clients.length}/${data.user.client_quota}`} />
                            <Stat label="Projects" value={`${data.projects.length}/${data.user.campaign_quota}`} />
                            <Stat label="Recent runs" value={data.recent_runs.length} />
                        </div>

                        <div className="px-6 pb-6">
                            <div className="label-micro mb-3 mt-2">Signup metadata</div>
                            <div className="card-base p-4 grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                                <Field k="Company" v={data.user.signup?.company_name} />
                                <Field k="Industry" v={data.user.signup?.industry} />
                                <Field k="Usage" v={data.user.signup?.usage_type} />
                                <Field k="Scale" v={data.user.signup?.scale} />
                                <Field k="Brand color" v={data.user.signup?.primary_color} />
                                <Field k="Created" v={timeAgo(data.user.created_at)} />
                            </div>
                        </div>

                        <Section title={`Clients (${data.clients.length})`}>
                            {data.clients.length === 0 ? (
                                <Empty>No clients yet.</Empty>
                            ) : (
                                <ul className="space-y-1.5">
                                    {data.clients.map(c => (
                                        <li key={c.id} className="px-3 py-2 border border-zinc-900 rounded-sm">
                                            <div className="text-sm">{c.company_name}</div>
                                            <div className="text-[10px] font-mono text-zinc-500 mt-0.5">{c.industry || "—"} · {timeAgo(c.created_at)}</div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </Section>

                        <Section title={`Projects (${data.projects.length})`}>
                            {data.projects.length === 0 ? (
                                <Empty>No projects yet.</Empty>
                            ) : (
                                <ul className="space-y-1.5">
                                    {data.projects.map(p => (
                                        <li key={p.id}>
                                            <Link to={`/projects/${p.id}`} className="block px-3 py-2 border border-zinc-900 hover:border-zinc-700 hover:bg-zinc-950 rounded-sm">
                                                <div className="text-sm flex items-center justify-between gap-2">
                                                    <span className="truncate">{p.title}</span>
                                                    <ArrowUpRight className="w-3 h-3 text-zinc-600 shrink-0" />
                                                </div>
                                                <div className="text-[10px] font-mono text-zinc-500 mt-0.5">
                                                    {p.project_type?.replaceAll("_", " ")} · {p.current_stage?.replaceAll("_", " ")} · {timeAgo(p.created_at)}
                                                </div>
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </Section>

                        <Section title="Recent activity">
                            {data.recent_runs.length === 0 ? (
                                <Empty>No agent runs yet.</Empty>
                            ) : (
                                <ul className="space-y-1.5">
                                    {data.recent_runs.map(r => (
                                        <li key={r.id} className="px-3 py-2 border border-zinc-900 rounded-sm flex items-center justify-between gap-2">
                                            <div className="min-w-0">
                                                <div className="text-sm truncate">{prettyAgent(r.agent)} · <span className="text-zinc-400">{r.stage?.replaceAll("_", " ")}</span></div>
                                                <div className="text-[10px] font-mono text-zinc-500 mt-0.5">{r.model || "—"} · {timeAgo(r.timestamp)}</div>
                                            </div>
                                            <span className={
                                                "px-2 py-0.5 rounded-sm border text-[10px] font-mono uppercase tracking-wider shrink-0 " +
                                                (r.status === "completed" ? "border-emerald-500/40 text-emerald-400" :
                                                 r.status === "failed" ? "border-red-500/40 text-red-400" :
                                                                          "border-amber-500/40 text-amber-400")
                                            }>{r.status}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </Section>
                    </>
                )}
            </aside>
            {pwOpen && data && data.user && (
                <ResetPasswordDialog
                    user={data.user}
                    onClose={() => setPwOpen(false)}
                    onSaved={async () => { setPwOpen(false); await onChange?.(); }}
                />
            )}
        </div>
    );
}

function Section({ title, children }) {
    return (
        <div className="px-6 pb-6">
            <div className="label-micro mb-3">{title}</div>
            {children}
        </div>
    );
}
function Field({ k, v }) {
    return (
        <>
            <span className="text-zinc-500">{k}</span>
            <span className="text-zinc-200 truncate">{v || "—"}</span>
        </>
    );
}

/* ─────────────────────────── Projects ─────────────────────────── */

function ProjectsSection() {
    const [items, setItems] = useState(null);
    useEffect(() => {
        api.get("/admin/projects").then(({ data }) => setItems(data)).catch(() => setItems([]));
    }, []);
    if (items === null) return <Loading />;
    if (items.length === 0) return <Empty>No projects.</Empty>;
    return (
        <div className="space-y-1.5" data-testid="admin-projects">
            {items.map(p => (
                <Link
                    key={p.id}
                    to={`/projects/${p.id}`}
                    data-testid={`admin-project-${p.id}`}
                    className="flex items-center justify-between gap-3 px-4 py-3 border border-zinc-900 hover:border-zinc-700 rounded-sm hover:bg-zinc-950 transition-colors"
                >
                    <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{p.title}</div>
                        <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mt-0.5">
                            {p.project_type?.replaceAll("_", " ")} · {p.current_stage?.replaceAll("_", " ")}
                        </div>
                    </div>
                    <div className="text-right">
                        <div className="text-xs text-zinc-300 truncate">{p.owner?.email || "—"}</div>
                        <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-600 mt-0.5 flex items-center gap-1 justify-end">
                            Open <ArrowUpRight className="w-2.5 h-2.5" />
                        </div>
                    </div>
                </Link>
            ))}
        </div>
    );
}

/* ─────────────────────────── LLM Health ─────────────────────────── */
/* Phase N.2 — Diagnostic + self-heal panel.
 * Surfaces the live status of the Emergent Universal Key, the proxy-side
 * budget cap (Current cost / Max budget), counts of stuck projects with
 * stale "Budget exceeded" errors, and a one-click "Verify & Clear" button
 * that fires a tiny probe and clears stale errors on success. NEVER
 * touches outputs, versions, approvals, or pipeline state — only stale
 * error strings.
 */
function LLMHealthSection() {
    const [snap, setSnap] = useState(null);
    const [busy, setBusy] = useState(false);
    const [lastVerify, setLastVerify] = useState(null);

    const reload = async () => {
        try {
            const { data } = await api.get("/admin/llm/health");
            setSnap(data);
        } catch (e) {
            toast.error("Could not load LLM health");
        }
    };
    useEffect(() => { reload(); }, []);

    const verify = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/admin/llm/verify");
            setLastVerify(data);
            if (data.ok) {
                toast.success(data.cleared_count > 0
                    ? `Cleared stale budget errors on ${data.cleared_count} project(s).`
                    : "Key works. No stale budget errors found.");
            } else {
                toast.error("Probe still failing — see budget details below.");
            }
            await reload();
        } catch (e) {
            toast.error(`Verify failed: ${e?.response?.data?.detail || e.message}`);
        } finally {
            setBusy(false);
        }
    };

    if (!snap) return <Loading />;
    const stuck = snap.projects_with_stale_budget_errors || 0;
    const budget = snap.last_observed_budget;
    const lastBudgetBlockTs = snap.last_budget_block_ts;
    const fmtTs = (ts) => ts ? new Date(ts * 1000).toLocaleString() : "—";

    return (
        <div className="space-y-6" data-testid="admin-llm-section">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Stat label="Universal Key" value={snap.key_last4 || "—"} />
                <Stat label="Strategic Model" value={snap.model_strategic?.replace("claude-","").slice(0,18) || "—"} />
                <Stat label="Quality-First" value={snap.quality_first_mode ? "ON" : "OFF"} accent={snap.quality_first_mode ? "good" : "warn"} />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Stat
                    label="Live availability"
                    value={snap.available ? "Available" : `Blocked: ${snap.reason || "unknown"}`}
                    accent={snap.available ? "good" : "warn"}
                />
                <Stat
                    label="Last successful call"
                    value={snap.last_success_ts ? fmtTs(snap.last_success_ts) : "Never (this session)"}
                />
                <Stat
                    label="Stale budget errors"
                    value={String(stuck)}
                    accent={stuck > 0 ? "warn" : "good"}
                />
            </div>

            {budget && Object.keys(budget).length > 0 && (
                <div className="p-4 border border-amber-500/30 bg-amber-500/5 rounded-sm space-y-2"
                     data-testid="admin-llm-budget-detail">
                    <div className="text-[10px] uppercase tracking-[0.2em] font-mono text-amber-300">
                        Last proxy-reported budget block
                    </div>
                    <div className="text-sm">
                        <span className="text-zinc-400">Max budget: </span>
                        <span className="text-amber-200 font-mono">${budget.max_budget?.toFixed(4) || "—"}</span>
                        <span className="text-zinc-700 mx-3">·</span>
                        <span className="text-zinc-400">Current cost: </span>
                        <span className="text-amber-200 font-mono">${budget.current_cost?.toFixed(4) || "—"}</span>
                        <span className="text-zinc-700 mx-3">·</span>
                        <span className="text-zinc-500">at {fmtTs(lastBudgetBlockTs)}</span>
                    </div>
                    <div className="text-xs text-zinc-400 leading-relaxed">
                        This is the LiteLLM virtual-key cap on the Emergent proxy — NOT a code constant.
                        If your wallet has been topped up, click <span className="text-orange-400">Verify &amp; Clear</span>:
                        a successful probe means the key is healthy and stale errors will be cleared from the project board.
                    </div>
                </div>
            )}

            <div className="flex flex-wrap items-center gap-3">
                <button
                    type="button"
                    onClick={verify}
                    disabled={busy}
                    data-testid="admin-llm-verify"
                    className="inline-flex items-center gap-2 px-4 py-2 text-xs font-mono uppercase tracking-wider rounded-sm bg-[#FF4400] text-white hover:bg-[#ff5722] disabled:opacity-50 transition-colors"
                >
                    <Zap className="w-3.5 h-3.5" />
                    {busy ? "Probing…" : "Verify & Clear stale errors"}
                </button>
                <button
                    type="button"
                    onClick={reload}
                    disabled={busy}
                    data-testid="admin-llm-refresh"
                    className="inline-flex items-center gap-2 px-3 py-2 text-xs font-mono uppercase tracking-wider rounded-sm border border-zinc-800 text-zinc-300 hover:bg-zinc-900"
                >
                    <RefreshCw className="w-3.5 h-3.5" /> Refresh
                </button>
            </div>

            {lastVerify && (
                <div className="p-4 border border-zinc-800 bg-zinc-950 rounded-sm space-y-2 text-xs"
                     data-testid="admin-llm-verify-result">
                    <div className="font-mono uppercase tracking-[0.18em] text-[10px] text-zinc-500">
                        Last verify result
                    </div>
                    <div className="text-zinc-200">
                        {lastVerify.ok ? "✓ " : "✗ "}{lastVerify.hint}
                    </div>
                    {lastVerify.probe?.error && (
                        <div className="font-mono text-[11px] text-amber-200 break-all">
                            {lastVerify.probe.error}
                        </div>
                    )}
                </div>
            )}

            <div className="p-4 border border-zinc-800 bg-zinc-950 rounded-sm text-xs text-zinc-400 leading-relaxed">
                <div className="font-mono uppercase tracking-[0.18em] text-[10px] text-zinc-500 mb-2">
                    What does this panel do?
                </div>
                <ul className="list-disc pl-5 space-y-1">
                    <li>Shows the current Universal Key (last 4 chars) and live availability.</li>
                    <li>If a recent agent call was blocked with <span className="text-amber-300">"Budget has been exceeded"</span>, the proxy-reported numbers are shown.</li>
                    <li>Click <span className="text-orange-400">Verify &amp; Clear</span> to fire a tiny test call and clear stale errors from all project boards on success.</li>
                    <li>Pipeline, agents, outputs, versions and approvals are <span className="text-zinc-300">never</span> modified.</li>
                </ul>
            </div>
        </div>
    );
}


/* ─────────────────────────── Activity ─────────────────────────── */

function ActivitySection() {
    const [items, setItems] = useState(null);
    useEffect(() => {
        api.get("/admin/activity", { params: { limit: 100 } })
            .then(({ data }) => setItems(data))
            .catch(() => setItems([]));
    }, []);
    if (items === null) return <Loading />;
    if (items.length === 0) return <Empty>No activity.</Empty>;
    return (
        <div className="card-base overflow-hidden" data-testid="admin-activity">
            <table className="w-full text-sm">
                <thead className="bg-zinc-950 text-[10px] font-mono uppercase tracking-wider text-zinc-500">
                    <tr>
                        <th className="text-left px-4 py-2">Agent</th>
                        <th className="text-left px-4 py-2">Project</th>
                        <th className="text-left px-4 py-2">Owner</th>
                        <th className="text-left px-4 py-2">Status</th>
                        <th className="text-left px-4 py-2">Model</th>
                        <th className="text-right px-4 py-2">When</th>
                    </tr>
                </thead>
                <tbody>
                    {items.map(r => (
                        <tr key={r.id} className="border-t border-zinc-900">
                            <td className="px-4 py-2 text-white">{prettyAgent(r.agent)}</td>
                            <td className="px-4 py-2 text-zinc-300 truncate max-w-[280px]">{r.project_title || r.project_id?.slice(0, 8)}</td>
                            <td className="px-4 py-2 text-zinc-400">{r.owner?.email || "—"}</td>
                            <td className="px-4 py-2">
                                <span className={
                                    "px-2 py-0.5 rounded-sm border text-[10px] font-mono uppercase tracking-wider " +
                                    (r.status === "completed" ? "border-emerald-500/40 text-emerald-400" :
                                     r.status === "failed"    ? "border-red-500/40 text-red-400" :
                                                                "border-amber-500/40 text-amber-400")
                                }>{r.status}</span>
                            </td>
                            <td className="px-4 py-2 font-mono text-[11px] text-zinc-500">{r.model || "—"}</td>
                            <td className="px-4 py-2 text-right text-[11px] font-mono text-zinc-500">{timeAgo(r.timestamp)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

/* ─────────────────────────── Telemetry (Phase N.7) ─────────────────────────── */

function TelemetrySection() {
    const [summary, setSummary] = useState(null);
    const [recent, setRecent] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = async () => {
        setBusy(true);
        try {
            const [s, r] = await Promise.all([
                api.get("/admin/telemetry/summary"),
                api.get("/admin/telemetry/recent", { params: { limit: 50 } }),
            ]);
            setSummary(s.data);
            setRecent(r.data || []);
        } catch (e) {
            toast.error(e.userMessage || e.message || "Telemetry load failed");
        } finally {
            setBusy(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    if (summary === null || recent === null) return <Loading />;

    const avgMs = summary.avg_stage_duration_ms;
    const avgLabel = avgMs == null
        ? "—"
        : avgMs < 1000 ? `${Math.round(avgMs)}ms`
        : avgMs < 60000 ? `${(avgMs / 1000).toFixed(1)}s`
        : `${(avgMs / 60000).toFixed(1)}m`;
    const mostFailing = summary.most_failing_stage
        ? `${prettyStage(summary.most_failing_stage)} (${summary.most_failing_stage_count})`
        : "—";
    const breakdown = summary.error_breakdown || {};

    return (
        <div className="space-y-6" data-testid="admin-telemetry">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="font-display text-2xl tracking-tight">Pipeline Telemetry</h2>
                    <div className="text-xs text-zinc-500 font-mono uppercase tracking-wider mt-1">
                        Window — last {summary.window_hours}h · Phase N.7
                    </div>
                </div>
                <button
                    type="button"
                    onClick={load}
                    disabled={busy}
                    data-testid="telemetry-refresh"
                    className="btn-secondary text-xs disabled:opacity-50 flex items-center gap-1.5"
                >
                    <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} />
                    {busy ? "Refreshing…" : "Refresh"}
                </button>
            </div>

            {/* Tiles */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <Tile label="Failures (24h)"      value={summary.failures_24h}    accent="rose"
                       testid="telemetry-failures-24h" />
                <Tile label="Most failing stage"  value={mostFailing}             accent="amber"
                       testid="telemetry-most-failing" />
                <Tile label="Repairs (24h)"       value={summary.repairs_24h}     accent="emerald"
                       testid="telemetry-repairs-24h" />
                <Tile label="Avg stage duration"  value={avgLabel}                accent="zinc"
                       testid="telemetry-avg-duration" />
            </div>

            {/* Error breakdown */}
            <div className="card-base p-5" data-testid="telemetry-error-breakdown">
                <div className="label-micro mb-3">Error breakdown (24h)</div>
                {Object.keys(breakdown).length === 0 ? (
                    <div className="text-sm text-zinc-500">No failures in the last 24h.</div>
                ) : (
                    <div className="flex flex-wrap gap-2">
                        {Object.entries(breakdown).map(([k, n]) => (
                            <span key={k}
                                   className="px-2 py-0.5 rounded-sm border border-zinc-700 text-zinc-200 text-[11px] font-mono">
                                {k}: <span className="text-rose-300">{n}</span>
                            </span>
                        ))}
                    </div>
                )}
            </div>

            {/* Recent events table */}
            <div className="card-base overflow-hidden" data-testid="telemetry-recent-table">
                <div className="px-4 py-3 border-b border-zinc-900 flex items-center justify-between">
                    <div className="label-micro">Recent events</div>
                    <div className="text-[11px] font-mono text-zinc-500">{recent.length} rows</div>
                </div>
                {recent.length === 0 ? (
                    <Empty>No telemetry events yet. Run a pipeline to populate this view.</Empty>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="bg-zinc-950 text-[10px] font-mono uppercase tracking-wider text-zinc-500">
                            <tr>
                                <th className="text-left px-4 py-2">Event</th>
                                <th className="text-left px-4 py-2">Stage</th>
                                <th className="text-left px-4 py-2">Project</th>
                                <th className="text-left px-4 py-2">Error</th>
                                <th className="text-right px-4 py-2">Duration</th>
                                <th className="text-right px-4 py-2">When</th>
                            </tr>
                        </thead>
                        <tbody>
                            {recent.map(r => (
                                <tr key={r.id} className="border-t border-zinc-900">
                                    <td className="px-4 py-2"><EventBadge type={r.event_type} /></td>
                                    <td className="px-4 py-2 text-zinc-300">
                                        {r.stage_key ? prettyStage(r.stage_key) :
                                            r.repair_action ? <span className="text-emerald-400">{r.repair_action}</span>
                                                            : <span className="text-zinc-600">—</span>}
                                    </td>
                                    <td className="px-4 py-2 font-mono text-[11px] text-zinc-500">
                                        {(r.project_id || "").slice(0, 8)}
                                    </td>
                                    <td className="px-4 py-2 text-zinc-400 text-[11px] font-mono">
                                        {r.error_type || "—"}
                                    </td>
                                    <td className="px-4 py-2 text-right text-[11px] font-mono text-zinc-500">
                                        {r.duration_ms == null ? "—" :
                                            r.duration_ms < 1000 ? `${r.duration_ms}ms` :
                                                                   `${(r.duration_ms / 1000).toFixed(1)}s`}
                                    </td>
                                    <td className="px-4 py-2 text-right text-[11px] font-mono text-zinc-500">
                                        {timeAgo(r.created_at || r.ts)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}

function Tile({ label, value, accent = "zinc", testid }) {
    const accents = {
        rose:    "text-rose-300",
        amber:   "text-amber-300",
        emerald: "text-emerald-300",
        zinc:    "text-zinc-200",
    };
    return (
        <div data-testid={testid} className="card-base p-4">
            <div className="label-micro mb-2">{label}</div>
            <div className={`font-display text-3xl tracking-tight truncate ${accents[accent]}`}>
                {value ?? "—"}
            </div>
        </div>
    );
}

function EventBadge({ type }) {
    const cls = type === "stage_completed"   ? "border-emerald-500/40 text-emerald-400"
              : type === "stage_failed"      ? "border-rose-500/40 text-rose-400"
              : type === "stage_started"     ? "border-zinc-700 text-zinc-400"
              : type === "pipeline_repaired" ? "border-amber-500/40 text-amber-300"
                                              : "border-zinc-700 text-zinc-500";
    return (
        <span className={`px-2 py-0.5 rounded-sm border text-[10px] font-mono uppercase tracking-wider ${cls}`}>
            {(type || "").replace("_", " ")}
        </span>
    );
}

function prettyStage(s) {
    if (!s) return "—";
    return s.replaceAll("_", " ").replace(/\b\w/g, c => c.toUpperCase());
}

/* ─────────────────────────── Shared ─────────────────────────── */

function StatusBadge({ status }) {
    if (status === "active") return <span className="px-2 py-0.5 rounded-sm border border-emerald-500/40 text-emerald-400 text-[10px] font-mono uppercase tracking-wider">Active</span>;
    if (status === "pending") return <span className="px-2 py-0.5 rounded-sm border border-amber-500/40 text-amber-400 text-[10px] font-mono uppercase tracking-wider">Pending</span>;
    return <span className="px-2 py-0.5 rounded-sm border border-zinc-700 text-zinc-500 text-[10px] font-mono uppercase tracking-wider">{status || "—"}</span>;
}
function PlanBadge({ plan, label }) {
    const cls = plan === "enterprise" ? "border-[#FF4400] text-[#FF4400]" :
                plan === "trial"      ? "border-amber-500 text-amber-400" :
                                        "border-zinc-700 text-zinc-500";
    return <span className={`px-2 py-0.5 rounded-sm border text-[10px] font-mono uppercase tracking-wider ${cls}`}>{label}</span>;
}
function Loading() { return <div className="text-sm text-zinc-500 py-10 text-center">Loading…</div>; }
function Empty({ children }) {
    return <div className="text-sm text-zinc-500 py-10 text-center border border-dashed border-zinc-900 rounded-sm">{children}</div>;
}
function prettyAgent(a) {
    if (!a) return "Agent";
    return a.replaceAll("_", " ").replace(/\b\w/g, c => c.toUpperCase());
}
function timeAgo(iso) {
    if (!iso) return "—";
    const d = (Date.now() - new Date(iso).getTime()) / 1000;
    if (d < 60) return `${Math.floor(d)}s ago`;
    if (d < 3600) return `${Math.floor(d / 60)}m ago`;
    if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
    return `${Math.floor(d / 86400)}d ago`;
}
